import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Col, Form, Input, Row, Table, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { EntitlementProjectionFailure } from '@/api/generated/schemas';
import type { RevenueCatBillingPeriodOut } from '@/api/generated/schemas';
import { BooleanFilter } from '@/components/common/BooleanFilter';
import { SelectFilter } from '@/components/common/SelectFilter';
import { TextFilter } from '@/components/common/TextFilter';
import { ShowLink } from '@/components/common/ShowLink';
import { ProjectionFailureTag } from '@/components/monetization/ProjectionFailureTag';
import { RevenueCatCustomerLink } from '@/components/monetization/RevenueCatLink';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useAdminTable } from '@/hooks/useAdminTable';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

const monospace = { fontFamily: 'monospace' };

const formatDateTime = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString() : '-';

export const RevenueCatBillingPeriodList = () => {
  const t = useTranslate();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<RevenueCatBillingPeriodOut>({
      pagination: { pageSize: 20 },
      resource: 'monetization.revenuecat_billing_periods',
      onSearch,
      sorters: {
        initial: [{ field: 'valid_until', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  return (
    <List canCreate={false}>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t(
                  'monetization.revenuecat_billing_periods.search.placeholder'
                )}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowLink
              resource="monetization.revenuecat_billing_periods"
              id={value}
            />
          )}
        />

        <Table.Column
          dataIndex="transaction_id"
          title={t(
            'monetization.revenuecat_billing_periods.fields.transaction_id'
          )}
          sorter
          sortOrder={getSortOrder(sorters, 'transaction_id')}
          render={(value: string) => <span style={monospace}>{value}</span>}
          filtered={isFieldFiltered(filters as CrudFilter[], 'transaction_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="transaction_id"
              width={220}
              placeholder={t(
                'monetization.revenuecat_billing_periods.filter.transaction_id'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="account_id"
          title={t('monetization.revenuecat_billing_periods.fields.account_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'account_id')}
          render={(value: number) => (
            <RevenueCatCustomerLink
              id={value}
              title={t(
                'monetization.revenuecat_billing_periods.actions.open_in_revenuecat'
              )}
              stopRowClick
            />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'account_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="account_id"
              width={160}
              placeholder={t(
                'monetization.revenuecat_billing_periods.filter.account_id'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="product_id"
          title={t('monetization.revenuecat_billing_periods.fields.product_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'product_id')}
          render={(value: number) => (
            <ShowLink resource="monetization.products" id={value} />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'product_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="product_id"
              width={160}
              placeholder={t(
                'monetization.revenuecat_billing_periods.filter.product_id'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="store_product_identifier"
          title={t(
            'monetization.revenuecat_billing_periods.fields.store_product_identifier'
          )}
          render={(value: string | null | undefined) => value ?? '-'}
        />

        <Table.Column
          dataIndex="subscription_id"
          title={t(
            'monetization.revenuecat_billing_periods.fields.subscription_id'
          )}
          sorter
          sortOrder={getSortOrder(sorters, 'subscription_id')}
          render={(value: number | null) =>
            value ? (
              <ShowLink
                resource="monetization.revenuecat_subscriptions"
                id={value}
              />
            ) : (
              '-'
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'subscription_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="subscription_id"
              width={160}
              placeholder={t(
                'monetization.revenuecat_billing_periods.filter.subscription_id'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="entitlement_id"
          title={t(
            'monetization.revenuecat_billing_periods.fields.entitlement_id'
          )}
          sorter
          sortOrder={getSortOrder(sorters, 'entitlement_id')}
          render={(value: number | null) =>
            value ? (
              <ShowLink resource="monetization.entitlements" id={value} />
            ) : (
              '-'
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'entitlement_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="entitlement_id"
              width={160}
              placeholder={t(
                'monetization.revenuecat_billing_periods.filter.entitlement_id'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="projection_failure_reason"
          title={t(
            'monetization.revenuecat_billing_periods.fields.projection_failure_reason'
          )}
          render={(value: EntitlementProjectionFailure | null) => (
            <ProjectionFailureTag value={value} />
          )}
          filtered={isFieldFiltered(
            filters as CrudFilter[],
            'projection_failure_reason'
          )}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="projection_failure_reason"
              minWidth={220}
              placeholder={t(
                'monetization.revenuecat_billing_periods.filter.projection_failure_reason'
              )}
              options={Object.values(EntitlementProjectionFailure).map(
                (reason) => ({
                  label: t(
                    `monetization.revenuecat_billing_periods.projection_failure.${reason}`
                  ),
                  value: reason,
                })
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="tier"
          title={t('monetization.revenuecat_billing_periods.fields.tier')}
          sorter
          sortOrder={getSortOrder(sorters, 'tier')}
          render={(value: string) => <Tag color="blue">{value}</Tag>}
        />

        <Table.Column
          dataIndex="status"
          title={t('monetization.revenuecat_billing_periods.fields.status')}
          sorter
          sortOrder={getSortOrder(sorters, 'status')}
          render={(value: string) => (
            <Tag color={value === 'active' ? 'success' : 'default'}>
              {t(`monetization.revenuecat_billing_periods.status.${value}`, {
                defaultValue: value,
              })}
            </Tag>
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'status')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="status"
              minWidth={180}
              placeholder={t(
                'monetization.revenuecat_billing_periods.filter.status'
              )}
              options={[
                {
                  label: t(
                    'monetization.revenuecat_billing_periods.status.active'
                  ),
                  value: 'active',
                },
                {
                  label: t(
                    'monetization.revenuecat_billing_periods.status.inactive'
                  ),
                  value: 'inactive',
                },
              ]}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="will_renew"
          title={t('monetization.revenuecat_billing_periods.fields.will_renew')}
          sorter
          sortOrder={getSortOrder(sorters, 'will_renew')}
          render={(value: boolean) => (
            <Tag color={value ? 'success' : 'default'}>
              {value ? t('common.yes') : t('common.no')}
            </Tag>
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'will_renew')}
          filterDropdown={({ confirm, clearFilters }) => (
            <BooleanFilter
              field="will_renew"
              placeholder={t(
                'monetization.revenuecat_billing_periods.filter.will_renew'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="valid_from"
          title={t('monetization.revenuecat_billing_periods.fields.valid_from')}
          sorter
          sortOrder={getSortOrder(sorters, 'valid_from')}
          render={formatDateTime}
        />

        <Table.Column
          dataIndex="valid_until"
          title={t(
            'monetization.revenuecat_billing_periods.fields.valid_until'
          )}
          sorter
          sortOrder={getSortOrder(sorters, 'valid_until')}
          render={formatDateTime}
        />

        <Table.Column
          dataIndex="store"
          title={t('monetization.revenuecat_billing_periods.fields.store')}
          sorter
          sortOrder={getSortOrder(sorters, 'store')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('monetization.revenuecat_billing_periods.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          render={formatDateTime}
        />

        <Table.Column
          dataIndex="updated_at"
          title={t('monetization.revenuecat_billing_periods.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          render={formatDateTime}
        />
      </Table>
    </List>
  );
};
